import { ElementSchema, ElementType } from "@schema-types/types/JourneySchema";
import { Question } from "./Question";
import { ContentElement } from "./Content";
import { ScriptElement } from "./Script";
import { AddressLookup } from "@forms/form-render/components/integrations/AddressLookup";
import { GooglePlaceSearch } from "@forms/form-render/components/integrations/GooglePlaceSearch";
import { VehicleSearch } from "@forms/form-render/components/integrations/VehicleSearch";
import { SharedoOdsSearch } from "@forms/form-render/components/integrations/SharedoOdsSearch";
import { InsuranceSearch } from "@forms/form-render/components/integrations/InsuranceSearch";
import { VonageVerifyEmailEntry } from "@forms/form-render/components/integrations/VonageVerifyEmailEntry";
import { VonageVerifyCodeEntry } from "@forms/form-render/components/integrations/VonageVerifyCodeEntry";
import { VonageVerifyInline } from "@forms/form-render/components/integrations/VonageVerifyInline";
import { IntegrationType } from "@schema-types/types/JourneySchema";
import * as Schema from "@schema-types/types/JourneySchema";

type ElementRendererProps = {
  element: ElementSchema;
  formStepId?: string;
  namePrefix?: string;
  hideElementTitles?: boolean;
  disabled?: boolean;
  enquiryId?: string;
  currentStepId?: string;
};

export const ElementRenderer = ({
  element,
  formStepId,
  namePrefix = "",
  hideElementTitles = false,
  disabled = false,
  enquiryId,
  currentStepId,
}: ElementRendererProps) => {
  if (element.type === ElementType.Question) {
    const questionElement = element as Schema.QuestionElementSchema;
    return (
      <Question
        key={element.id}
        title={hideElementTitles ? "" : element.title}
        elementDisplayId={element.displayId}
        fieldSchemas={questionElement.fields.map((field) => {
          if (field.type === "Input") {
            return {
              ...field,
              name: namePrefix ? `${namePrefix}.${field.name}` : field.name,
            };
          }
          return field;
        })}
        disabled={disabled}
      />
    );
  } else if (element.type === ElementType.Content) {
    return <ContentElement key={element.id} element={element} formStepId={formStepId ?? ""} />;
  } else if (element.type === ElementType.Script) {
    return <ScriptElement key={element.id} element={element} />;
  } else if (element.type === ElementType.Integration) {
    const integrationElement = element as Schema.IntegrationElementSchema;
    const name = namePrefix ? `${namePrefix}.${element.displayId}` : element.displayId;

    if (integrationElement.integrationType === IntegrationType.GoogleAddressSearch) {
      return (
        <div key={element.id} className="jb-integration space-y-3">
          {!hideElementTitles && <h3 className="text-lg font-medium">{element.title}</h3>}
          <AddressLookup
            label={element.title}
            name={name}
            required={integrationElement.config?.required ?? false}
            disabled={disabled}
            countries={integrationElement.config?.countries as string[] | undefined}
            helperText={integrationElement.config?.helperText as string | undefined}
            addressFieldsRequired={integrationElement.config?.addressFieldsRequired !== false}
          />
        </div>
      );
    } else if (integrationElement.integrationType === IntegrationType.GooglePlaceSearch) {
      return (
        <div key={element.id} className="jb-integration space-y-3">
          {!hideElementTitles && <h3 className="text-lg font-medium">{element.title}</h3>}
          <GooglePlaceSearch
            label={element.title}
            name={name}
            required={integrationElement.config?.required ?? false}
            disabled={disabled}
            helperText={integrationElement.config?.helperText as string | undefined}
            placeTypes={Array.isArray(integrationElement.config?.placeTypes) ? (integrationElement.config.placeTypes as string[]) : []}
            showManualEntryButton={integrationElement.config?.showManualEntryButton !== false}
          />
        </div>
      );
    } else if (integrationElement.integrationType === IntegrationType.VehicleSearch) {
      return (
        <div key={element.id} className="jb-integration space-y-3">
          {!hideElementTitles && <h3 className="text-lg font-medium">{element.title}</h3>}
          <VehicleSearch
            label={element.title}
            name={name}
            required={integrationElement.config?.required ?? false}
            disabled={disabled}
            helperText={integrationElement.config?.helperText as string | undefined}
            showManualEntryButton={integrationElement.config?.showManualEntryButton === true}
          />
        </div>
      );
    } else if (integrationElement.integrationType === IntegrationType.SharedoOdsSearch) {
      return (
        <div key={element.id} className="jb-integration space-y-3">
          {!hideElementTitles && <h3 className="text-lg font-medium">{element.title}</h3>}
          <SharedoOdsSearch
            label={element.title}
            name={name}
            required={integrationElement.config?.required ?? false}
            disabled={disabled}
            helperText={integrationElement.config?.helperText as string | undefined}
            entityType={integrationElement.config?.entityType === "organisation" ? "organisation" : "person"}
            checkExistingMatters={integrationElement.config?.checkExistingMatters !== false}
            showManualEntryButton={integrationElement.config?.showManualEntryButton === true}
          />
        </div>
      );
    } else if (integrationElement.integrationType === IntegrationType.InsuranceSearch) {
      return (
        <div key={element.id} className="jb-integration space-y-3">
          {!hideElementTitles && <h3 className="text-lg font-medium">{element.title}</h3>}
          <InsuranceSearch
            label={element.title}
            name={name}
            required={integrationElement.config?.required ?? false}
            disabled={disabled}
            helperText={integrationElement.config?.helperText as string | undefined}
          />
        </div>
      );
    } else if (integrationElement.integrationType === IntegrationType.VonageVerifyEmailEntry) {
      return (
        <div key={element.id} className="jb-integration space-y-3">
          {!hideElementTitles && <h3 className="text-lg font-medium">{element.title}</h3>}
          <VonageVerifyEmailEntry
            label={element.title}
            required={integrationElement.config?.required ?? false}
            disabled={disabled}
            helperText={integrationElement.config?.helperText as string | undefined}
            showInfoMessage={integrationElement.config?.showInfoMessage !== false}
            name=""
          />
        </div>
      );
    } else if (integrationElement.integrationType === IntegrationType.VonageVerifyCodeEntry) {
      return (
        <div key={element.id} className="jb-integration space-y-3">
          {!hideElementTitles && <h3 className="text-lg font-medium">{element.title}</h3>}
          <VonageVerifyCodeEntry
            label={element.title}
            name={name}
            required={integrationElement.config?.required ?? false}
            disabled={disabled}
            helperText={integrationElement.config?.helperText as string | undefined}
            codeLength={integrationElement.config?.codeLength ?? 6}
            enquiryId={enquiryId}
            currentStepId={currentStepId}
          />
        </div>
      );
    } else if (integrationElement.integrationType === IntegrationType.VonageVerifyInline) {
      return (
        <div key={element.id} className="jb-integration space-y-3">
          {!hideElementTitles && <h3 className="text-lg font-medium">{element.title}</h3>}
          <VonageVerifyInline
            label={element.title}
            name={`${name}_verification`}
            required={integrationElement.config?.required ?? false}
            disabled={disabled}
            helperText={integrationElement.config?.helperText as string | undefined}
            codeLength={integrationElement.config?.codeLength ?? 4}
            enquiryId={enquiryId}
            currentStepId={currentStepId}
          />
        </div>
      );
    }
    return null;
  }
  return null;
};
